import React from 'react';

export interface KeyValueItem {
  key: string;
  value: string | number | React.ReactNode;
  hidden?: boolean;
}

interface KeyValueTableProps {
  title?: string;
  items: KeyValueItem[];
  variant?: 'default' | 'compact';
  className?: string;
  style?: React.CSSProperties;
  showBorder?: boolean;
}

const KeyValueTable: React.FC<KeyValueTableProps> = ({
  title,
  items,
  variant = 'default',
  className,
  style,
  showBorder = true,
}) => {
  const visibleItems = items.filter(item => !item.hidden);

  if (visibleItems.length === 0) {
    return null;
  }

  const containerStyle: React.CSSProperties = {
    marginBottom: variant === 'compact' ? 'var(--plexus-space-sm)' : 'var(--plexus-space-md)',
    backgroundColor: 'var(--plexus-color-surface-50)',
    borderRadius: 'var(--plexus-border-radius-md)',
    border: showBorder ? `var(--plexus-border-width-thin) solid var(--plexus-color-neutral-600)` : 'none',
    overflow: 'hidden',
    ...style,
  };

  const headerStyle: React.CSSProperties = {
    padding: variant === 'compact' ? 'var(--plexus-space-sm)' : 'var(--plexus-space-md)',
    borderBottom: `var(--plexus-border-width-thin) solid var(--plexus-color-neutral-600)`,
    backgroundColor: 'var(--plexus-color-surface-100)',
    fontSize: '1.125rem',
    fontWeight: 600,
  };

  const titleStyle: React.CSSProperties = {
    fontSize: variant === 'compact' ? '1rem' : '1.25rem',
    fontWeight: 600,
    color: 'var(--plexus-color-neutral-100)',
  };

  const keyStyle: React.CSSProperties = {
    flex: '0 0 150px',
    fontSize: '0.875rem',
    color: 'var(--plexus-color-neutral-200)',
    fontWeight: 500,
    paddingRight: 'var(--plexus-space-md)',
  };

  const valueStyle: React.CSSProperties = {
    flex: '1',
    fontSize: '1rem',
    fontWeight: 600,
    color: 'var(--plexus-color-neutral-100)',
  };

  return (
    <div className={className} style={containerStyle}>
      {title && (
        <div style={headerStyle}>
          <div style={titleStyle}>{title}</div>
        </div>
      )}

      <div style={{ padding: 0 }}>
        {visibleItems.map((item, index) => (
          <div
            key={item.key}
            style={{
              display: 'flex',
              padding: variant === 'compact' ? 'var(--plexus-space-sm)' : 'var(--plexus-space-md)',
              borderBottom: index < visibleItems.length - 1 
                ? `var(--plexus-border-width-thin) solid var(--plexus-color-neutral-600)` 
                : 'none',
            }}
          >
            <div style={keyStyle}>
              {item.key}
            </div>
            <div style={valueStyle}>
              {item.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default KeyValueTable;